import { Link } from "react-router-dom";
import { Wallet, AlertTriangle, CheckCircle2, Clock, XCircle, MessageCircle } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { formatCOP } from "../lib/api";
import { openWhatsApp, whatsappUrl } from "../lib/whatsapp";

const STATUS_CONFIG = {
  verde: {
    label: "Al día",
    icon: CheckCircle2,
    cardClass: "bg-green-50 border-green-200",
    iconClass: "text-green-600",
    badgeClass: "bg-green-100 text-green-800 border-green-300",
  },
  amarillo: {
    label: "Atención",
    icon: Clock,
    cardClass: "bg-amber-50 border-amber-200",
    iconClass: "text-amber-600",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-300",
  },
  rojo: {
    label: "Vencida",
    icon: AlertTriangle,
    cardClass: "bg-red-50 border-red-200",
    iconClass: "text-red-600",
    badgeClass: "bg-red-100 text-red-800 border-red-300",
  },
  gris: {
    label: "Sin cupo",
    icon: XCircle,
    cardClass: "bg-cm-cream-alt border-cm-border",
    iconClass: "text-cm-ink-soft",
    badgeClass: "bg-white text-cm-ink-soft border-cm-border",
  },
};

export const CarteraSummaryCard = ({ cartera, customer }) => {
  if (!cartera) return null;
  const cfg = STATUS_CONFIG[cartera.status] || STATUS_CONFIG.gris;
  const Icon = cfg.icon;
  const usedPct = cartera.cupo > 0 ? Math.min((cartera.saldo_total / cartera.cupo) * 100, 100) : 0;

  return (
    <Card className={`p-5 border shadow-none ${cfg.cardClass}`} data-testid="cartera-summary-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`h-10 w-10 rounded-md bg-white/60 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${cfg.iconClass}`} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium">Mi cartera</p>
            <Badge variant="outline" className={`text-[10px] ${cfg.badgeClass}`}>
              {cfg.label}
            </Badge>
          </div>
          <p className="font-medium text-cm-ink text-sm mt-1">{cartera.mensaje}</p>
          {cartera.nota_credito && (
            <p className="mt-2 text-[11px] text-cm-ink-soft italic" data-testid="cartera-nota-credito">
              ℹ {cartera.nota_credito}
            </p>
          )}
          {cartera.cupo > 0 && cartera.n_facturas > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Cupo</div>
                <div className="font-mono font-semibold text-cm-ink">{formatCOP(cartera.cupo)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Disponible</div>
                <div className="font-mono font-semibold text-cm-ink">{formatCOP(cartera.disponible)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Saldo</div>
                <div className="font-mono font-semibold text-cm-ink">{formatCOP(cartera.saldo_total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Vencido</div>
                <div className={`font-mono font-semibold ${cartera.saldo_vencido > 0 ? "text-red-700" : "text-cm-ink"}`}>
                  {formatCOP(cartera.saldo_vencido)}
                </div>
              </div>
            </div>
          )}
          {cartera.cupo > 0 && cartera.n_facturas === 0 && (
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-testid="cartera-summary-basic">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Cupo de crédito</div>
                <div className="font-mono font-semibold text-cm-ink">{formatCOP(cartera.cupo)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Plazo</div>
                <div className="font-mono font-semibold text-cm-ink">{cartera.plazo_dias} días</div>
              </div>
            </div>
          )}
          {cartera.cupo > 0 && cartera.n_facturas > 0 && (
            <div className="mt-3" data-testid="cartera-usage-bar">
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div
                  className={`h-full ${cartera.status === "rojo" ? "bg-red-500" : cartera.status === "amarillo" ? "bg-amber-500" : "bg-green-500"}`}
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <p className="text-[10px] text-cm-ink-soft mt-1 font-mono">{usedPct.toFixed(0)}% del cupo usado · Plazo {cartera.plazo_dias} días</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Link
            to="/mi-cartera"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-cm-ink text-cm-cream rounded text-xs font-medium hover:bg-cm-ink/90 transition-colors"
            data-testid="cartera-card-detail-link"
          >
            <Wallet className="h-3.5 w-3.5" /> Ver detalle
          </Link>
          {cartera.status === "rojo" && customer && (
            <a
              href={whatsappUrl("573045729333", `Hola, soy ${customer.razon_social || customer.almacen || "cliente"} (NIT ${customer.nit}) y quiero consultar mi estado de cartera.`)}
              onClick={(e) => {
                e.preventDefault();
                openWhatsApp("573045729333", `Hola, soy ${customer.razon_social || customer.almacen || "cliente"} (NIT ${customer.nit}) y quiero consultar mi estado de cartera.`);
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
              data-testid="cartera-card-whatsapp"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Cartera
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};

export { STATUS_CONFIG };
